import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import {
    CognitoUserPool,
    CognitoUser,
    AuthenticationDetails,
    CognitoUserAttribute,
    ISignUpResult,
    CognitoUserSession
} from 'amazon-cognito-identity-js';

// Configure the Cognito User Pool
const poolData = {
    UserPoolId: import.meta.env.VITE_COGNITO_USER_POOL_ID,
    ClientId: import.meta.env.VITE_COGNITO_CLIENT_ID,
};

const userPool = new CognitoUserPool(poolData);

// Types
export interface HostedAuthUser {
    username: string;
    email?: string;
    attributes?: Record<string, string>;
    session?: CognitoUserSession;
}

export interface SignUpData {
    email: string;
    password: string;
    givenName?: string;
    familyName?: string;
    phoneNumber?: string;
}

export interface SignInData {
    email: string;
    password: string;
}

export interface HostedAuthContextType {
    user: HostedAuthUser | null;
    isLoading: boolean;
    isAuthenticated: boolean;
    signUp: (data: SignUpData) => Promise<ISignUpResult>;
    signIn: (data: SignInData) => Promise<CognitoUserSession>;
    signOut: () => void;
    confirmSignUp: (email: string, code: string) => Promise<any>;
    resendConfirmationCode: (email: string) => Promise<any>;
    forgotPassword: (email: string) => Promise<any>;
    confirmPassword: (email: string, code: string, newPassword: string) => Promise<any>;
    getCurrentSession: () => Promise<CognitoUserSession | null>;
}

const HostedAuthContext = createContext<HostedAuthContextType | undefined>(undefined);

export const useHostedAuth = () => {
    const context = useContext(HostedAuthContext);
    if (!context) {
        throw new Error('useHostedAuth must be used within HostedAuthProvider');
    }
    return context;
};

interface HostedAuthProviderProps {
    children: React.ReactNode;
}

export const HostedAuthProvider: React.FC<HostedAuthProviderProps> = ({ children }) => {
    const [user, setUser] = useState<HostedAuthUser | null>(null);
    const [isLoading, setIsLoading] = useState(true);

    // Get current session
    const getCurrentSession = useCallback(async (): Promise<CognitoUserSession | null> => {
        const cognitoUser = userPool.getCurrentUser();
        if (!cognitoUser) {
            return null;
        }

        return new Promise((resolve, reject) => {
            cognitoUser.getSession((err: Error | null, session: CognitoUserSession | null) => {
                if (err) {
                    reject(err);
                    return;
                }
                resolve(session);
            });
        });
    }, []);

    // Load user session on mount
    useEffect(() => {
        const loadUser = async () => {
            try {
                const cognitoUser = userPool.getCurrentUser();
                if (!cognitoUser) {
                    setIsLoading(false);
                    return;
                }

                const session = await getCurrentSession();
                if (session && session.isValid()) {
                    // Get user attributes
                    cognitoUser.getUserAttributes((err, attributes) => {
                        if (err) {
                            console.error('Error getting user attributes:', err);
                            setIsLoading(false);
                            return;
                        }

                        const attrs: Record<string, string> = {};
                        attributes?.forEach(attr => {
                            attrs[attr.Name] = attr.Value;
                        });

                        setUser({
                            username: cognitoUser.getUsername(),
                            email: attrs['email'],
                            attributes: attrs,
                            session
                        });
                        setIsLoading(false);
                    });
                } else {
                    setIsLoading(false);
                }
            } catch (error) {
                console.error('Error loading user session:', error);
                setIsLoading(false);
            }
        };

        loadUser();
    }, [getCurrentSession]);

    // Sign up
    const signUp = useCallback(async (data: SignUpData): Promise<ISignUpResult> => {
        const attributeList: CognitoUserAttribute[] = [
            new CognitoUserAttribute({ Name: 'email', Value: data.email }),
        ];

        // Construct full name from given and family name
        const fullName = `${data.givenName || ''} ${data.familyName || ''}`.trim();
        if (fullName) {
            attributeList.push(new CognitoUserAttribute({ Name: 'name', Value: fullName }));
        }

        if (data.givenName) {
            attributeList.push(new CognitoUserAttribute({ Name: 'given_name', Value: data.givenName }));
        }
        if (data.familyName) {
            attributeList.push(new CognitoUserAttribute({ Name: 'family_name', Value: data.familyName }));
        }
        if (data.phoneNumber) {
            attributeList.push(new CognitoUserAttribute({ Name: 'phone_number', Value: data.phoneNumber }));
        }

        return new Promise((resolve, reject) => {
            userPool.signUp(
                data.email,
                data.password,
                attributeList,
                [],
                (err, result) => {
                    if (err) {
                        reject(err);
                        return;
                    }
                    if (!result) {
                        reject(new Error('Sign up failed'));
                        return;
                    }
                    resolve(result);
                }
            );
        });
    }, []);

    // Sign in
    const signIn = useCallback(async (data: SignInData): Promise<CognitoUserSession> => {
        const authenticationDetails = new AuthenticationDetails({
            Username: data.email,
            Password: data.password,
        });

        const cognitoUser = new CognitoUser({
            Username: data.email,
            Pool: userPool,
        });

        return new Promise((resolve, reject) => {
            cognitoUser.authenticateUser(authenticationDetails, {
                onSuccess: (session: CognitoUserSession) => {
                    // Get user attributes
                    cognitoUser.getUserAttributes((err, attributes) => {
                        if (err) {
                            console.error('Error getting user attributes:', err);
                        }

                        const attrs: Record<string, string> = {};
                        attributes?.forEach(attr => {
                            attrs[attr.Name] = attr.Value;
                        });

                        setUser({
                            username: cognitoUser.getUsername(),
                            email: attrs['email'],
                            attributes: attrs,
                            session
                        });
                        resolve(session);
                    });
                },
                onFailure: (err: Error) => {
                    reject(err);
                },
                newPasswordRequired: (_userAttributes, _requiredAttributes) => {
                    // Handle new password required
                    reject(new Error('New password required'));
                },
            });
        });
    }, []);

    // Sign out
    const signOut = useCallback(() => {
        const cognitoUser = userPool.getCurrentUser();
        if (cognitoUser) {
            cognitoUser.signOut();
        }
        setUser(null);
    }, []);

    // Confirm sign up
    const confirmSignUp = useCallback(async (email: string, code: string): Promise<any> => {
        const cognitoUser = new CognitoUser({
            Username: email,
            Pool: userPool,
        });

        return new Promise((resolve, reject) => {
            cognitoUser.confirmRegistration(code, true, (err, result) => {
                if (err) {
                    reject(err);
                    return;
                }
                resolve(result);
            });
        });
    }, []);

    // Resend confirmation code
    const resendConfirmationCode = useCallback(async (email: string): Promise<any> => {
        const cognitoUser = new CognitoUser({
            Username: email,
            Pool: userPool,
        });

        return new Promise((resolve, reject) => {
            cognitoUser.resendConfirmationCode((err, result) => {
                if (err) {
                    reject(err);
                    return;
                }
                resolve(result);
            });
        });
    }, []);

    // Forgot password
    const forgotPassword = useCallback(async (email: string): Promise<any> => {
        const cognitoUser = new CognitoUser({
            Username: email,
            Pool: userPool,
        });

        return new Promise((resolve, reject) => {
            cognitoUser.forgotPassword({
                onSuccess: (result) => {
                    resolve(result);
                },
                onFailure: (err) => {
                    reject(err);
                },
            });
        });
    }, []);

    // Confirm password
    const confirmPassword = useCallback(async (email: string, code: string, newPassword: string): Promise<any> => {
        const cognitoUser = new CognitoUser({
            Username: email,
            Pool: userPool,
        });

        return new Promise((resolve, reject) => {
            cognitoUser.confirmPassword(code, newPassword, {
                onSuccess: () => {
                    resolve(true);
                },
                onFailure: (err) => {
                    reject(err);
                },
            });
        });
    }, []);

    const value: HostedAuthContextType = {
        user,
        isLoading,
        isAuthenticated: !!user,
        signUp,
        signIn,
        signOut,
        confirmSignUp,
        resendConfirmationCode,
        forgotPassword,
        confirmPassword,
        getCurrentSession,
    };

    return (
        <HostedAuthContext.Provider value={value}>
            {children}
        </HostedAuthContext.Provider>
    );
};
